import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaLinkedin, 
  FaInstagram, 
  FaFacebookSquare, 
  FaYoutube, 
  FaWhatsapp, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaGlobe, 
  FaShareAlt, 
  FaCheck, 
  FaQrcode, 
  FaMapMarkerAlt,
  FaTimes,
  FaStar,
  FaCheckDouble,
  FaArrowRight
} from 'react-icons/fa';
import './ConnectSocials.css';

const SOCIAL_PLATFORMS = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    handle: '@imatrix-t',
    desc: 'Official company announcements, industry trends & professional network',
    url: 'https://in.linkedin.com/company/imatrix-t',
    icon: <FaLinkedin />,
    brandColor: '#0a66c2',
    accentBg: 'rgba(10, 102, 194, 0.08)',
    tag: 'Corporate Network'
  },
  {
    id: 'instagram',
    name: 'Instagram',
    handle: '@imatrix.t',
    desc: 'Company culture, technology highlights & event celebrations',
    url: 'https://www.instagram.com/imatrix.t/',
    icon: <FaInstagram />,
    brandColor: '#e1306c',
    accentBg: 'rgba(225, 48, 108, 0.08)',
    tag: 'Culture & Stories'
  },
  {
    id: 'youtube',
    name: 'YouTube',
    handle: '@imatrixtechnologies1833',
    desc: 'TallyPrime video tutorials, webinars & product feature demos',
    url: 'https://www.youtube.com/@imatrixtechnologies1833/featured',
    icon: <FaYoutube />,
    brandColor: '#dc2626',
    accentBg: 'rgba(220, 38, 38, 0.08)',
    tag: 'Webinars & Tutorials'
  },
  {
    id: 'facebook',
    name: 'Facebook',
    handle: '@imatrix.pvt',
    desc: 'Community engagement, technology news & product launches',
    url: 'https://www.facebook.com/imatrix.pvt',
    icon: <FaFacebookSquare />,
    brandColor: '#1877f2',
    accentBg: 'rgba(24, 119, 242, 0.08)',
    tag: 'Community Hub'
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    handle: '+91 98416 09990',
    desc: 'Instant consultation, software license quotes & priority helpdesk',
    url: 'https://wa.me/919841609990?text=Hello%20iMatrix%20Technologies,%20I%20scanned%20your%20QR%20code%20and%20would%20like%20to%20connect!',
    icon: <FaWhatsapp />,
    brandColor: '#16a34a',
    accentBg: 'rgba(22, 163, 74, 0.08)',
    tag: 'Instant Chat'
  }
];

const STATS = [
  { value: '20+', label: 'Years Serving Clients' },
  { value: '3000+', label: 'Trusted Businesses' },
  { value: '5-Star', label: 'Tally Certified Partner' },
];

const ConnectSocials = () => {
  const [copied, setCopied] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : 'https://imatrixtechnologies.com/connect';
  const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(currentUrl)}&bgcolor=FFFFFF&color=2A2C24&margin=12`;

  const handleShare = async () => {
    if (navigator?.share) {
      try {
        await navigator.share({
          title: 'Connect with iMatrix Technologies',
          text: 'Official social profiles and contact channels for iMatrix Technologies Pvt Ltd.',
          url: currentUrl,
        });
      } catch (err) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(currentUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
        return;
      }

      const tempTextArea = document.createElement('textarea');
      tempTextArea.value = currentUrl;
      tempTextArea.setAttribute('readonly', '');
      tempTextArea.style.position = 'fixed';
      tempTextArea.style.top = '-9999px';
      tempTextArea.style.left = '-9999px';
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      document.execCommand('copy');
      document.body.removeChild(tempTextArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (error) {
      console.error('Unable to copy URL to clipboard:', error);
    }
  };

  const shareQrCode = async () => {
    try {
      if (!navigator?.share) {
        await copyToClipboard();
        return;
      }

      const response = await fetch(qrCodeImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'imatrix-connect-qr.png', { type: blob.type || 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'iMatrix Technologies Connect',
          text: 'Scan this QR code to open the official iMatrix Connect page',
          url: currentUrl,
          files: [file],
        });
        return;
      }

      await navigator.share({
        title: 'iMatrix Technologies Connect',
        text: 'Scan this QR code to open the official iMatrix Connect page',
        url: currentUrl,
      });
    } catch (error) {
      console.error('Unable to share QR code:', error);
      await copyToClipboard();
    }
  };

  return (
    <div className="connect-page-wrapper">
      {/* Subtle organic ambient background layers matching Hero Section */}
      <div className="connect-hero-bg" aria-hidden="true" />
      <div className="connect-hero-grid" aria-hidden="true" />
      <div className="connect-hero-swoosh" aria-hidden="true" />

      <main className="connect-main-container">
        {/* Top Control Bar */}
        <div className="connect-top-bar">
          <div className="connect-badge">
            <FaCheckDouble className="connect-badge__icon" />
            <span>Official Connect Hub</span>
          </div>

          <div className="connect-top-actions">
            <button 
              className="action-btn-pill" 
              onClick={() => setShowQrModal(true)}
              title="Show QR Code"
              aria-label="Show QR Code"
            >
              <FaQrcode />
              <span>QR Code</span>
            </button>

            <button 
              className="action-btn-pill" 
              onClick={handleShare}
              title="Share page"
              aria-label="Share page"
            >
              {copied ? <FaCheck style={{ color: '#16a34a' }} /> : <FaShareAlt />}
              <span>{copied ? 'Copied' : 'Share'}</span>
            </button>
          </div>
        </div>

        {/* Brand Banner Card */}
        <section className="connect-brand-card">
          <div className="brand-card-left">
            <div className="connect-logo-box">
              <img 
                src="/images/Customer_logo/imatrix_white_logo.png" 
                alt="iMatrix Technologies" 
                className="connect-logo-img"
              />
            </div>
            
            <div className="brand-card-text">
              <div className="brand-badge-row">
                <span className="partner-star-badge">
                  <FaStar className="star-icon" /> 5-Star Tally Certified Partner
                </span>
                <span className="since-tag">Since. 2006</span>
              </div>
              <h1 className="brand-title">
                iMatrix Technologies <span className="brand-subname">Pvt Ltd</span>
              </h1>
              <p className="brand-description">
                Delivering enterprise-grade IT infrastructure, TallyPrime customization, cloud hosting, 
                and bespoke software solutions across India.
              </p>
            </div>
          </div>

          {/* Quick Stats Strip */}
          <div className="connect-stats-strip">
            {STATS.map((s, idx) => (
              <div key={idx} className="stat-box">
                <span className="stat-number">{s.value}</span>
                <span className="stat-caption">{s.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 2-Column Wide Layout */}
        <div className="connect-content-grid">
          {/* Left Column: Official Social Profiles */}
          <div className="connect-col-left">
            <div className="col-header">
              <h2>Official Social Media Channels</h2>
              <p>Follow our official feeds for tutorials, product updates, and business insights</p>
            </div>

            <div className="social-cards-grid">
              {SOCIAL_PLATFORMS.map((platform) => (
                <a
                  key={platform.id}
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-tile-card"
                  style={{
                    '--tile-color': platform.brandColor,
                    '--tile-accent': platform.accentBg
                  }}
                >
                  <div className="tile-icon-box" style={{ color: platform.brandColor }}>
                    {platform.icon}
                  </div>

                  <div className="tile-body">
                    <div className="tile-head-row">
                      <h3 className="tile-title">{platform.name}</h3>
                      <span className="tile-tag">{platform.tag}</span>
                    </div>
                    <div className="tile-handle">{platform.handle}</div>
                    <p className="tile-desc">{platform.desc}</p>
                  </div>

                  <div className="tile-arrow">
                    <FaArrowRight />
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right Column: Direct Communication, Office & Quick Website */}
          <div className="connect-col-right">
            <div className="col-header">
              <h2>Direct Contact & Office</h2>
              <p>Get in touch directly with our support & sales representatives</p>
            </div>

            <div className="direct-cards-wrapper">
              {/* Main Website Card */}
              <Link to="/" className="direct-feature-card">
                <div className="feature-icon-circle">
                  <FaGlobe />
                </div>
                <div className="feature-card-info">
                  <span className="feature-kicker">Official Portal</span>
                  <h3 className="feature-heading">Explore Full Website</h3>
                  <p className="feature-sub">Browse our complete lineup of IT solutions, Tally add-ons & services.</p>
                </div>
                <div className="feature-card-btn">
                  <span>Visit</span>
                  <FaArrowRight />
                </div>
              </Link>

              {/* Phone & Email Dual Grid */}
              <div className="dual-contact-row">
                <a href="tel:+919841609990" className="contact-tile-action">
                  <div className="contact-icon-bubble">
                    <FaPhoneAlt />
                  </div>
                  <div>
                    <span className="contact-tile-label">Call Our Helpdesk</span>
                    <span className="contact-tile-val">+91 98416 09990</span>
                  </div>
                </a>

                <a href="mailto:admin@imatrixtechnologies.com" className="contact-tile-action">
                  <div className="contact-icon-bubble">
                    <FaEnvelope />
                  </div>
                  <div>
                    <span className="contact-tile-label">Send Email</span>
                    <span className="contact-tile-val">admin@imatrix...</span>
                  </div>
                </a>
              </div>

              {/* Office Location Card */}
              <a 
                href="https://www.google.com/maps?ll=13.068722,80.216376&z=16&t=m&hl=en&gl=IN&mapclient=embed&cid=10554500636439021061" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="office-location-card"
              >
                <div className="office-icon-bubble">
                  <FaMapMarkerAlt />
                </div>
                <div className="office-text-body">
                  <span className="office-title">Chennai Corporate Office</span>
                  <address className="office-address">
                    R 76, Water Tank Rd, MMDA Colony, Arumbakkam, Chennai, Tamil Nadu 600106
                  </address>
                </div>
                <div className="office-action-link">
                  <span>Maps</span>
                  <FaArrowRight />
                </div>
              </a>

              {/* Embedded In-Page Scannable QR Mini-Card */}
              <div className="qr-embedded-card">
                <div className="qr-embedded-img-box" onClick={() => setShowQrModal(true)}>
                  <img src={qrCodeImageUrl} alt="QR Code" className="qr-mini-img" />
                </div>
                <div className="qr-embedded-text">
                  <h4>Scan & Share On The Go</h4>
                  <p>Open your smartphone camera to save or bookmark this page anytime.</p>
                  <button className="qr-expand-btn" onClick={() => setShowQrModal(true)}>
                    <FaQrcode /> View Full QR Code
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Clean Footer Bar */}
        <footer className="connect-page-footer">
          <p>© {new Date().getFullYear()} iMatrix Technologies Pvt Ltd. All rights reserved.</p>
          <div className="footer-links-row">
            <Link to="/AboutPage">About Us</Link>
            <span>•</span>
            <Link to="/tallyprime">Tally Solutions</Link>
            <span>•</span>
            <Link to="/Client">Clients</Link>
            <span>•</span>
            <Link to="/contact">Contact</Link>
          </div>
        </footer>
      </main>

      {/* QR Code Popup Modal */}
      {showQrModal && (
        <div className="qr-modal-backdrop" onClick={() => setShowQrModal(false)}>
          <div className="qr-modal-box" onClick={(e) => e.stopPropagation()}>
            <button 
              className="qr-modal-dismiss" 
              onClick={() => setShowQrModal(false)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <div className="qr-modal-head">
              <span className="partner-star-badge" style={{ marginBottom: '8px' }}>
                <FaStar className="star-icon" /> iMatrix Official Connect
              </span>
              <h3>Scan With Smartphone</h3>
              <p>Direct your camera at the QR code below to immediately load all official platform links.</p>
            </div>

            <div className="qr-modal-image-wrapper">
              <img 
                src={qrCodeImageUrl} 
                alt="iMatrix Social QR Code" 
                className="qr-modal-code-img"
              />
            </div>

            <span className="qr-code-url-label">{currentUrl}</span>

            <div className="qr-modal-footer-actions">
              <button className="modal-share-btn" onClick={shareQrCode}>
                <FaShareAlt />
                <span>Share QR</span>
              </button>

              <button className="modal-copy-btn" onClick={copyToClipboard}>
                {copied ? <FaCheck /> : <FaShareAlt />}
                <span>{copied ? 'Link Copied to Clipboard!' : 'Copy Link URL'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectSocials;
